"use client";

import { toIndonesianOrdinal } from "@/utils/helper";
import { AnimatePresence, motion } from "framer-motion";
import React, { Fragment, useEffect, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { FaChevronDown, FaPlus, FaTrash } from "react-icons/fa6";

type FieldType = "text" | "date" | "number" | "select";

interface FieldTemplate {
  withCounter?: boolean;
  name: string;
  type: FieldType;
  placeholder?: string;
  label?: string;
  defaultValue?: any[];
  required_asterik?: boolean;
  options?: { label: string; value: string | number }[]; // only for select
}

interface MultiFieldGroupProps {
  label?: string;
  template: FieldTemplate[];
  error: any;
  max?: number; // ✅ optional max row count
}

const FormMultifield: React.FC<MultiFieldGroupProps> = ({
  label,
  template,
  error,
  max,
}) => {
  const initialRowCount = template[0]?.defaultValue?.length || 0;

  const [show, setShow] = useState(true);
  const [rowCount, setRowCount] = useState(initialRowCount);

  const { control, register, setValue, unregister, getValues } =
    useFormContext();

  useEffect(() => {
    template.forEach((tpl) => {
      const initialValues = tpl.defaultValue || [];
      initialValues.slice(0, initialRowCount).forEach((value, index) => {
        setValue(
          `${tpl.name}.${index}`,
          value === null || value === undefined ? "" : String(value)
        );
      });
      for (let i = initialRowCount; i < initialValues.length; i++) {
        unregister(`${tpl.name}.${i}`);
      }
    });
  }, [initialRowCount, setValue, template, unregister]);

  const handleAdd = () => {
    if (max !== undefined && rowCount >= max) return;

    const newIndex = rowCount;
    template.forEach((tpl) => {
      const initialValue = tpl.defaultValue?.[newIndex];
      setValue(
        `${tpl.name}.${newIndex}`,
        initialValue === null || initialValue === undefined
          ? ""
          : String(initialValue)
      );
    });
    setRowCount(rowCount + 1);
  };

  const handleRemove = (indexToRemove: number) => {
    if (rowCount > 0) {
      template.forEach((tpl) => {
        const fieldName = tpl.name;
        for (let i = indexToRemove; i < rowCount - 1; i++) {
          const nextValue = getValues(`${fieldName}.${i + 1}`);
          setValue(
            `${fieldName}.${i}`,
            nextValue === null || nextValue === undefined
              ? ""
              : String(nextValue)
          );
        }
        setValue(`${fieldName}.${rowCount - 1}`, "");
        unregister(`${fieldName}.${rowCount - 1}`);
      });
      setRowCount(rowCount - 1);
    }
  };

  useEffect(() => {
    template.forEach((tpl) => {
      const currentValues = getValues(tpl.name) as any[];
      if (Array.isArray(currentValues) && currentValues.length > rowCount) {
        for (let i = rowCount; i < currentValues.length; i++) {
          unregister(`${tpl.name}.${i}`);
        }
        setValue(tpl.name, currentValues.slice(0, rowCount));
      } else if (
        Array.isArray(currentValues) &&
        currentValues.length < rowCount
      ) {
        for (let i = currentValues.length; i < rowCount; i++) {
          setValue(`${tpl.name}.${i}`, "");
        }
      } else if (rowCount > 0 && !Array.isArray(getValues(tpl.name))) {
        const initial = [];
        for (let i = 0; i < rowCount; i++) {
          initial.push("");
          setValue(`${tpl.name}.${i}`, "");
        }
        setValue(tpl.name, initial);
      }
    });
  }, [rowCount, setValue, template, unregister, control]);

  const keys = template?.map((i) => i.name);
  const error_field = Array.from(new Set(keys))
    .map((i) => error[i]?.message ?? "")
    ?.filter((i) => i != "");

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        className="btn btn-xs flex items-center justify-between btn-neutral btn-block"
        onClick={() => setShow(!show)}
      >
        {label} <FaChevronDown className={show ? "rotate-180" : ""} />
      </button>

      <AnimatePresence>
        {show && (
          <motion.div
            className={`p-4 rounded-lg border-2 ${
              error_field?.length > 0 ? "border-error" : "border-base-300"
            }`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {[...Array(rowCount)].map((_, index) => (
              <Fragment key={index}>
                {index !== 0 && <hr className="mb-2" />}
                <div className="flex items-start gap-2 mb-3">
                  <div className="grid lg:grid-cols-2 gap-4 flex-1">
                    {template.map((tpl) => {
                      const fieldPath = `${tpl.name}.${index}` as const;

                      return (
                        <div key={fieldPath} className="flex flex-col gap-2">
                          <label htmlFor={fieldPath} className="text-[0.6rem]">
                            {tpl.label}{" "}
                            {tpl.withCounter
                              ? ` ${toIndonesianOrdinal(index + 1)}`
                              : ""}
                            {tpl.required_asterik && (
                              <span className="text-error">*</span>
                            )}
                          </label>

                          {tpl.type === "select" ? (
                            <Controller
                              name={fieldPath}
                              control={control}
                              render={({ field }) => (
                                <select
                                  {...field}
                                  className="select-xs select select-primary w-full"
                                >
                                  <option value="">Select...</option>
                                  {tpl.options?.map((opt) => (
                                    <option
                                      key={opt.value}
                                      value={String(opt.value)}
                                    >
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              )}
                            />
                          ) : (
                            <input
                              type={tpl.type === "number" ? "text" : tpl.type}
                              placeholder={tpl.placeholder}
                              {...register(
                                fieldPath,
                                tpl.type === "number"
                                  ? { pattern: /^[0-9]*$/ }
                                  : {}
                              )}
                              className="input input-primary input-xs w-full"
                            />
                          )}

                          {error?.[tpl.name]?.[index]?.message && (
                            <span className="text-error text-xs">
                              {error[tpl.name][index]?.message}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    className="btn btn-error mt-6 text-white btn-xs"
                  >
                    <FaTrash />
                  </button>
                </div>
              </Fragment>
            ))}

            <button
              type="button"
              onClick={handleAdd}
              className="btn btn-secondary btn-xs btn-block mt-2"
              disabled={max !== undefined && rowCount >= max}
            >
              <FaPlus /> Add
            </button>

            {error_field.length > 0 && (
              <div className="text-error text-xs mt-2">{error_field[0]}</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FormMultifield;
